import React, { useRef, useEffect, useState } from 'react';
import { TextField, Tooltip, IconButton } from '@material-ui/core';
import { renderDate, dateRangeFilter } from "common/services/util";
import DateRangeFilter from 'common/components/DateRangeFilter';
import IvaSwitch from 'invoices/components/IvaSwitch';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CheckIcon from '@material-ui/icons/Check';

const PortalCopyCell = ({ email }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        const url = email
            ? `${window.location.origin}/portal?email=${encodeURIComponent(email)}`
            : `${window.location.origin}/portal`
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <Tooltip title={copied ? '¡Enlace copiado!' : 'Copiar enlace del portal'}>
            <IconButton size="small" onClick={handleCopy} style={{ padding: 4 }}>
                {copied
                    ? <CheckIcon fontSize="small" style={{ color: '#2e7d32' }} />
                    : <VpnKeyIcon fontSize="small" style={{ color: '#555' }} />
                }
            </IconButton>
        </Tooltip>
    );
};

const communityByPostcode = (postcode) => {
    const prefix = parseInt(postcode.substring(0, 2), 10);
    const map = {
        1: 'País Vasco', 2: 'Castilla-La Mancha', 3: 'Comunidad Valenciana',
        4: 'Andalucía', 5: 'Castilla y León', 6: 'Extremadura',
        7: 'Islas Baleares', 8: 'Cataluña', 9: 'Castilla y León',
        10: 'Extremadura', 11: 'Andalucía', 12: 'Comunidad Valenciana',
        13: 'Castilla-La Mancha', 14: 'Andalucía', 15: 'Galicia',
        16: 'Castilla-La Mancha', 17: 'Cataluña', 18: 'Andalucía',
        19: 'Castilla-La Mancha', 20: 'País Vasco', 21: 'Andalucía',
        22: 'Aragón', 23: 'Andalucía', 24: 'Castilla y León',
        25: 'Cataluña', 26: 'La Rioja', 27: 'Galicia',
        28: 'Comunidad de Madrid', 29: 'Andalucía', 30: 'Región de Murcia',
        31: 'Comunidad Foral de Navarra', 32: 'Galicia', 33: 'Asturias',
        34: 'Castilla y León', 35: 'Canarias', 36: 'Galicia',
        37: 'Castilla y León', 38: 'Canarias', 39: 'Cantabria',
        40: 'Castilla y León', 41: 'Andalucía', 42: 'Castilla y León',
        43: 'Cataluña', 44: 'Aragón', 45: 'Castilla-La Mancha',
        46: 'Comunidad Valenciana', 47: 'Castilla y León', 48: 'País Vasco',
        49: 'Castilla y León', 50: 'Aragón', 51: 'Ceuta', 52: 'Melilla'
    };
    return map[prefix] || '';
};

const PostcodeEditComponent = ({ value, onChange, rowData }) => {
    // Mantener siempre la referencia actualizada al rowData más reciente
    const rowDataRef = useRef(rowData);
    useEffect(() => { rowDataRef.current = rowData; });

    const handleChange = async (e) => {
        const postcode = e.target.value;
        onChange(postcode); // actualiza el campo postcode inmediatamente
        if (postcode.length === 5) {
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?postalcode=${postcode}&countrycodes=es&format=json&addressdetails=1&limit=1`,
                    { headers: { 'Accept-Language': 'es' } }
                );
                if (res.ok) {
                    const json = await res.json();
                    const address = json[0]?.address;
                    if (address) {
                        const locality = address.city || address.town || address.village || address.municipality || '';
                        rowDataRef.current.locality = locality;
                        rowDataRef.current.community = communityByPostcode(postcode);
                        onChange(postcode);
                    }
                }
            } catch {}
        }
    };

    return (
        <TextField
            value={value || ''}
            onChange={handleChange}
            inputProps={{ maxLength: 5 }}
        />
    );
};

export const columnsClients = (checkedSelected, checkAll, onIvaToggle) => [
    {
        title: '',
        field: 'selected',
        render: props => checkedSelected(props),
        editable: 'never',
        cellStyle: { width: '30px'},
        filterComponent: () => checkAll()
    },
    { title: 'Nombre', field: 'name'},
    { title: 'DNI', field: 'id', editable: 'onAdd' },
    { title: 'Calle', field: 'street'},
    { title: 'Cod. postal', field: 'postcode', editComponent: props => <PostcodeEditComponent {...props} /> },
    { title: 'Localidad', field: 'locality'},
    { title: 'Com. Autonoma', field: 'community'},
    { title: 'Telefono', field: 'phone'},
    { title: 'Email', field: 'mail'},
    { title: 'Cod. Cliente', field: 'codClient'},
    {
        title: 'Pago defecto',
        field: 'defaultPaymentMode',
        lookup: { 0: 'Efectivo', 1: 'Tarjeta', 2: 'Transferencia' },
    },
    {
        title: 'IVA',
        field: 'iva',
        render: rowData => rowData ? (
            <IvaSwitch
                checked={rowData.iva !== false}
                onChange={() => onIvaToggle(rowData)}
            />
        ) : null,
        editable: 'never'
    },
    {
        title: 'Portal',
        field: 'portalEnabled',
        editable: 'never',
        filtering: false,
        cellStyle:   { width: 48, minWidth: 48, maxWidth: 48, padding: '0 4px', textAlign: 'center' },
        headerStyle: { width: 48, minWidth: 48, maxWidth: 48, padding: '0 4px', textAlign: 'center' },
        render: rowData => rowData?.mail
            ? <PortalCopyCell email={rowData.mail} />
            : <span style={{ color: '#ccc', fontSize: '12px' }}>—</span>
    },
    {
        title: 'Ult. modificacion', field: 'lastUpdated', editable: 'never',
        filterComponent: props => <DateRangeFilter {...props} />,
        customFilterAndSearch: (filter, rowData) => dateRangeFilter(filter, rowData.lastUpdated),
        render: rowData => rowData ? renderDate(rowData.lastUpdated) : "",
        defaultSort: 'desc'
    }
]
